(() => {
  'use strict';

  document.addEventListener('DOMContentLoaded', () => {
    const modal = document.getElementById('CarposterCheckoutModal');
    if (!modal) return;

    const content = modal.querySelector('[data-cp-checkout-modal-content]');
    const loading = modal.querySelector('[data-cp-checkout-modal-loading]');
    const closeButtons = modal.querySelectorAll('[data-cp-checkout-modal-close]');
    let lastTrigger = null;
    let requestController = null;
    let lockedScrollY = 0;
    let mobilePageMode = false;
    let previousThemeColor = null;

    const closeTransientCartUi = () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();

      const notification = document.querySelector('cart-notification');
      if (notification && typeof notification.close === 'function') notification.close();
    };

    const setLoading = () => {
      modal.classList.remove('is-loaded');
      if (!loading) return;
      loading.classList.remove('is-error');
      loading.innerHTML = `
        <span class="cp-checkout-modal__spinner" aria-hidden="true"></span>
        <span>Зареждаме поръчката...</span>
      `;
    };

    const showLoadError = () => {
      if (!loading) return;
      loading.classList.add('is-error');
      loading.innerHTML = '<span>Не успяхме да заредим поръчката. Моля, затворете прозореца и опитайте отново.</span>';
    };

    const renderCheckout = async () => {
      if (!content) return;

      requestController?.abort();
      requestController = new AbortController();
      setLoading();
      content.replaceChildren();

      try {
        /*
         * Shopify Section Rendering API renders the checkout section against
         * the CURRENT cart cookie. No /pages/checkout page is required.
         */
        const root = window.Shopify?.routes?.root || '/';
        const separator = root.includes('?') ? '&' : '?';
        const url = `${root}${separator}section_id=carposter-checkout&_=${Date.now()}`;

        const response = await fetch(url, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' },
          signal: requestController.signal,
        });

        if (!response.ok) {
          throw new Error(`Checkout section failed (${response.status})`);
        }

        const html = await response.text();
        if (!html || !html.includes('cp-checkout')) {
          throw new Error('Checkout section response is empty or invalid.');
        }

        const template = document.createElement('template');
        template.innerHTML = html.trim();

        /* Scripts inserted through innerHTML do not execute; checkout JS is
           already loaded globally by theme.liquid and is initialized below. */
        template.content.querySelectorAll('script[src*="carposter-checkout.js"]').forEach((el) => el.remove());
        template.content.querySelectorAll('link[href*="carposter-checkout.css"]').forEach((el) => el.remove());

        content.replaceChildren(template.content.cloneNode(true));

        const checkoutRoot = content.querySelector('.cp-checkout[data-section-id]');
        if (!checkoutRoot) throw new Error('Checkout root was not rendered.');
        checkoutRoot.classList.add('cp-checkout--embedded');

        document.dispatchEvent(new CustomEvent('carposter:checkout-mounted', {
          detail: { root: content },
        }));

        modal.classList.add('is-loaded');
        content.scrollTop = 0;
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[CarPoster checkout modal] Could not render checkout.', error);
        showLoadError();
      }
    };

    const open = async (trigger) => {
      lastTrigger = trigger || document.activeElement;
      closeTransientCartUi();

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cp-checkout-modal-open');

      if (window.matchMedia('(max-width: 760px)').matches) {
        mobilePageMode = true;
        lockedScrollY = window.scrollY || window.pageYOffset || 0;

        // Mobile checkout behaves like a real page, not a modal overlay.
        modal.classList.add('cp-checkout-modal--mobile-page');
        document.documentElement.classList.add('cp-checkout-mobile-page-open');
        document.body.classList.add('cp-checkout-mobile-page-open');
        document.documentElement.classList.remove('cp-checkout-mobile-open');

        const dialog = modal.querySelector('.cp-checkout-modal__dialog');
        if (dialog) {
          dialog.setAttribute('role', 'region');
          dialog.removeAttribute('aria-modal');
        }

        const themeColor = document.querySelector('meta[name="theme-color"]');
        if (themeColor) {
          previousThemeColor = themeColor.getAttribute('content');
          themeColor.setAttribute('content', '#ffffff');
        }

        // The storefront is hidden in page mode, so start the checkout at top.
        window.scrollTo(0, 0);
      }

      await renderCheckout();

      window.setTimeout(() => {
        modal.querySelector('.cp-checkout-modal__close')?.focus();
      }, 0);
    };

    const close = () => {
      if (!modal.classList.contains('is-open')) return;

      requestController?.abort();
      requestController = null;

      modal.classList.remove('is-open', 'is-loaded');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cp-checkout-modal-open');
      document.documentElement.classList.remove('cp-checkout-mobile-open');

      if (mobilePageMode) {
        modal.classList.remove('cp-checkout-modal--mobile-page');
        document.documentElement.classList.remove('cp-checkout-mobile-page-open');
        document.body.classList.remove('cp-checkout-mobile-page-open');

        const dialog = modal.querySelector('.cp-checkout-modal__dialog');
        if (dialog) {
          dialog.setAttribute('role', 'dialog');
          dialog.setAttribute('aria-modal', 'true');
        }

        const themeColor = document.querySelector('meta[name="theme-color"]');
        if (themeColor && previousThemeColor !== null) {
          themeColor.setAttribute('content', previousThemeColor);
        }
        previousThemeColor = null;
        mobilePageMode = false;
        window.scrollTo(0, lockedScrollY);
      }

      const wasFixed = document.body.style.position === 'fixed';
      if (wasFixed) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.background = '';
      }

      content?.replaceChildren();

      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        window.setTimeout(() => lastTrigger.focus(), 0);
      }
    };

    /* Event delegation survives Sense cart-drawer re-renders. */
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-carp-custom-checkout]');
      if (!trigger) return;

      event.preventDefault();
      open(trigger);
    });

    closeButtons.forEach((button) => button.addEventListener('click', close));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  });
})();
